import styled, { css } from 'styled-components';
import { slideUp, fadeIn, breathe, focusVisible } from '../Dashboard/animations';
import { Table, TableHead, TableRow, TableCell } from './table';
import { Card } from './card';

// Frosted glass card wrapper
export const StyledTableShell = styled(Card)`
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.68);
  box-shadow:
    0 16px 40px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(14px);
  overflow-x: hidden;
`;

// Styled table with spacing
export const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
`;

// Table head with uppercase styling
export const StyledTableHead = styled(TableHead)`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  padding: 0.75rem 0.75rem;
`;

// Animated table row with optional accent border
export const StyledTableRow = styled(TableRow)<{
  $status?: string;
  $accentColor?: string;
  $animate?: boolean;
}>`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  ${props => props.$accentColor && css`border-left: 4px solid ${props.$accentColor};`}
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease, border-left-width 200ms ease;
  ${props => props.$animate !== false && css`
    animation: ${slideUp} 300ms ease-out backwards, ${fadeIn} 400ms ease-out backwards;
  `}

  ${focusVisible}

  td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }

  td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
    border-color: rgba(34, 123, 126, 0.5);
    ${props => props.$accentColor && css`border-left-width: 5px;`}
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &:hover, &:active {
      transform: none;
      ${props => props.$accentColor && css`border-left-width: 4px;`}
    }
  }

  @media (max-width: 640px) {
    ${props => props.$accentColor && css`border-left-width: 3px;`}

    &:hover {
      ${props => props.$accentColor && css`border-left-width: 4px;`}
    }

    @media (prefers-reduced-motion: reduce) {
      &:hover {
        ${props => props.$accentColor && css`border-left-width: 3px;`}
      }
    }

    td {
      padding: 0.65rem;
    }

    td:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    td:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
`;

// Simple table row for admin tables (no accent border, just fadeIn)
export const SimpleStyledTableRow = styled(TableRow)`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  transition: transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease;
  ${css`animation: ${fadeIn} 400ms ease-out backwards;`}

  ${focusVisible}

  td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }

  td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
    border-color: rgba(34, 123, 126, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    &:hover, &:active {
      transform: none;
    }
  }

  @media (max-width: 640px) {
    td {
      padding: 0.65rem;
    }

    td:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    td:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }
`;

// Styled table cell
export const StyledTableCell = styled(TableCell)`
  padding: 0.9rem 0.85rem;
  font-size: 0.9rem;
  color: #0f172a;
  border: none;
`;

// Muted text for secondary info
export const MutedText = styled.div`
  color: #64748b;
  font-size: 0.78rem;
  margin-top: 0.15rem;
`;

// Empty state
export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: #64748b;
  font-size: 0.95rem;
  background: #f8f9fa;
  border-radius: 8px;
  ${css`animation: ${breathe} 3s ease-in-out infinite;`}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Status badge
export const StatusBadge = styled.span<{ $variant: string }>`
  ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background-color: rgba(34, 197, 94, 0.16) !important;
          color: #166534 !important;
          border: 1px solid rgba(34, 197, 94, 0.28);
        `;
      case 'danger':
        return `
          background-color: rgba(239, 68, 68, 0.16) !important;
          color: #991b1b !important;
          border: 1px solid rgba(239, 68, 68, 0.28);
        `;
      case 'warning':
        return `
          background-color: rgba(245, 158, 11, 0.16) !important;
          color: #92400e !important;
          border: 1px solid rgba(245, 158, 11, 0.28);
        `;
      case 'info':
        return `
          background-color: rgba(14, 165, 233, 0.16) !important;
          color: #075985 !important;
          border: 1px solid rgba(14, 165, 233, 0.28);
        `;
      case 'primary':
        return `
          background-color: rgba(34, 123, 126, 0.16) !important;
          color: #0f4d4f !important;
          border: 1px solid rgba(34, 123, 126, 0.28);
        `;
      default: // neutral
        return `
          background-color: rgba(148, 163, 184, 0.2) !important;
          color: #334155 !important;
          border: 1px solid rgba(148, 163, 184, 0.3);
        `;
    }
  }}
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 150ms ease-in-out;

  tr:hover & {
    transform: scale(1.02);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    tr:hover & {
      transform: none;
    }
  }
`;

// Helper function for status color mapping
export const getStatusAccentColor = (status: string): string => {
  const statusUpper = status.toUpperCase().trim().replace(/_/g, ' ');

  // Match status colors
  if (statusUpper === 'OFFER ACCEPTED' || statusUpper === 'PLACED' || statusUpper === 'ACCEPTED') {
    return '#22c55e'; // Success green
  }
  if (statusUpper === 'REJECTED' || statusUpper === 'AGENCY REJECTED' || statusUpper === 'WITHDRAWN') {
    return '#ef4444'; // Danger red
  }
  if (statusUpper === 'OFFER EXTENDED' || statusUpper === 'SHORTLISTED') {
    return '#f59e0b'; // Warning orange
  }
  if (statusUpper === 'INTERVIEWING') {
    return '#0ea5e9'; // Info blue
  }
  if (statusUpper === 'NEW') {
    return '#227B7E'; // Primary teal
  }

  // Candidate/Role specific status
  if (statusUpper === 'PLATFORM INCLUDED') {
    return '#f5a623'; // Orange
  }
  if (statusUpper === 'SHARED') {
    return 'rgb(0, 176, 0)'; // Green
  }

  return 'rgba(148, 163, 184, 0.55)'; // Neutral gray
};

// Status variant helper
export const getStatusVariant = (status: string): string => {
  const statusUpper = status.toUpperCase().trim().replace(/_/g, ' ');

  if (statusUpper === 'OFFER ACCEPTED' || statusUpper === 'PLACED' || statusUpper === 'ACCEPTED' || statusUpper === 'SHARED') {
    return 'success';
  }
  if (statusUpper === 'REJECTED' || statusUpper === 'AGENCY REJECTED' || statusUpper === 'WITHDRAWN') {
    return 'danger';
  }
  if (statusUpper === 'OFFER EXTENDED' || statusUpper === 'SHORTLISTED' || statusUpper === 'PLATFORM INCLUDED') {
    return 'warning';
  }
  if (statusUpper === 'INTERVIEWING') {
    return 'info';
  }
  if (statusUpper === 'NEW') {
    return 'primary';
  }
  return 'neutral';
};

// Sortable table head with direction indicator
export const SortableTableHead = styled(StyledTableHead)<{
  $sorted?: 'asc' | 'desc' | null;
  $sortable?: boolean;
}>`
  cursor: ${props => props.$sortable !== false ? 'pointer' : 'default'};
  user-select: none;
  transition: color 150ms ease;
  position: relative;
  padding-right: ${props => props.$sortable !== false ? '1.5rem' : '0.75rem'};

  &:hover {
    color: ${props => props.$sortable !== false ? '#0f172a' : '#64748b'};
  }

  &::after {
    content: ${props => {
      if (props.$sortable === false) return '""';
      if (props.$sorted === 'asc') return '"↑"';
      if (props.$sorted === 'desc') return '"↓"';
      return '"↕"';
    }};
    position: absolute;
    right: 0.5rem;
    opacity: ${props => props.$sorted ? 1 : 0.3};
    font-size: 0.7rem;
    transition: opacity 150ms ease;
  }

  &:hover::after {
    opacity: ${props => props.$sortable !== false ? 0.8 : 0.3};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::after {
      transition: none;
    }
  }
`;
